import React, { useState, useEffect, useCallback } from 'react';
import { useSupportTickets } from '../../hooks/useSupportTickets';
import { useAuthContext } from '../../contexts/AuthContext';
import { useUserProfileContext } from '../../contexts/UserProfileContext';
import { ProfileType } from '../../types/enums';
import Button from '../../components/Button/Button';
import InputField from '../../components/InputField/InputField';
import TextArea from '../../components/TextArea/TextArea';
import Dropdown from '../../components/Dropdown/Dropdown';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import SupportContactCard from '../../components/SupportContactCard/SupportContactCard';
import { isSupportTicketsEnabled } from '../../lib/support/supportContact';
import styles from './SupportPage.module.css';

interface Ticket {
    id: number;
    ticketNumber: string;
    title: string;
    category: string;
    status: string;
    statusColor: string;
    createdAt: string;
    hasUnreadReplies: boolean;
}

interface TicketDetail {
    id: number;
    ticketNumber: string;
    title: string;
    status: string;
    canClose: boolean;
    canReopen: boolean;
    messages: Array<{
        id: number;
        senderName: string;
        senderType: string;
        isCustomer: boolean;
        body: string;
        createdAt: string;
    }>;
}

// #1211: Sanitise text - reject characters that enable XSS
const containsXssChars = (value: string): boolean => /[<>&]/.test(value);
const sanitizeInput = (value: string): string => value.replace(/[<>&]/g, '');

const SupportTicketsPage: React.FC = () => {
    const { getCategories, createTicket, getTickets, getTicketById, replyToTicket, closeTicket, loading, error: ticketsError } = useSupportTickets();
    const { userType } = useAuthContext();
    const { profile } = useUserProfileContext();
    const isSuperAdmin = userType === ProfileType.SuperAdmin;

    const [view, setView] = useState<'list' | 'create' | 'detail'>('list');
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
    const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);

    // Create form state - #1185: added subject field
    const [subject, setSubject] = useState('');
    const [category, setCategory] = useState('');
    const [description, setDescription] = useState('');
    const [replyText, setReplyText] = useState('');

    // #1156: form validation errors
    const [formErrors, setFormErrors] = useState<{ subject?: string; category?: string; description?: string }>({});

    const loadTickets = useCallback(async () => {
        const result = await getTickets();
        setTickets(result.tickets || []);
    }, [getTickets]);

    const loadCategories = useCallback(async () => {
        const cats = await getCategories();
        setCategories(cats);
    }, [getCategories]);

    useEffect(() => {
        loadTickets();
        loadCategories();
    }, [loadTickets, loadCategories]);

    // #1156: validate form fields before submission
    const validateForm = (): boolean => {
        const errors: { subject?: string; category?: string; description?: string } = {};
        if (!subject.trim()) errors.subject = 'Subject is required.';
        else if (subject.trim().length < 3) errors.subject = 'Subject must be at least 3 characters.';
        else if (containsXssChars(subject)) errors.subject = 'Subject must not contain special characters.';
        if (!category) errors.category = 'Please select a category.';
        if (!description.trim()) errors.description = 'Description is required.';
        else if (description.trim().length < 10) errors.description = 'Description must be at least 10 characters.';
        else if (containsXssChars(description)) errors.description = 'Description must not contain special characters.';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateTicket = async () => {
        if (!validateForm()) return;
        try {
            // #1154: include email in API payload; #1185: include subject as title
            await createTicket({
                category,
                description: sanitizeInput(description),
                email: profile?.email || '',
                fullName: profile?.fullName || '',
                preferredContactMethod: 'Email',
            });
            setSubject('');
            setCategory('');
            setDescription('');
            setFormErrors({});
            setView('list');
            await loadTickets();
        } catch {
            // Error handled in hook
        }
    };

    const handleViewTicket = async (ticketId: number) => {
        const detail = await getTicketById(ticketId);
        if (detail) {
            setSelectedTicket(detail);
            setView('detail');
        }
    };

    const handleReply = async () => {
        if (!selectedTicket || !replyText.trim()) return;
        const success = await replyToTicket(selectedTicket.id, replyText);
        if (success) {
            setReplyText('');
            const updated = await getTicketById(selectedTicket.id);
            if (updated) setSelectedTicket(updated);
        }
    };

    const handleCloseTicket = async () => {
        if (!selectedTicket) return;
        const success = await closeTicket(selectedTicket.id);
        if (success) {
            setView('list');
            await loadTickets();
        }
    };

    if (view === 'create') {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    <h2>Submit Support Ticket</h2>
                    {/* #1186: use ghost variant instead of secondary (dark/black) to match design system */}
                    <Button label="Back to Tickets" variant="ghost" onClick={() => setView('list')} />
                </div>
                <div className={styles.form}>
                    {/* #1185: Subject/Title field */}
                    <InputField
                        label="Subject"
                        name="subject"
                        value={subject}
                        onChange={(v) => { setSubject(v); setFormErrors(prev => ({ ...prev, subject: undefined })); }}
                        placeholder="Brief summary of your issue"
                        required
                        feedback={formErrors.subject ? 'error' : undefined}
                        feedbackMessage={formErrors.subject}
                    />
                    <Dropdown
                        label="Category"
                        options={categories.map(c => c.name)}
                        value={category}
                        onChange={(val) => { setCategory(val as string); setFormErrors(prev => ({ ...prev, category: undefined })); }}
                    />
                    {formErrors.category && <span className={styles.fieldError}>{formErrors.category}</span>}
                    <TextArea
                        label="Description"
                        value={description}
                        onChange={(v) => { setDescription(v); setFormErrors(prev => ({ ...prev, description: undefined })); }}
                        placeholder="Describe your issue in detail..."
                    />
                    {formErrors.description && <span className={styles.fieldError}>{formErrors.description}</span>}
                    <Button
                        label={loading ? 'Submitting...' : 'Submit Ticket'}
                        variant="primary"
                        onClick={handleCreateTicket}
                        disabled={loading}
                    />
                </div>
            </div>
        );
    }

    if (view === 'detail' && selectedTicket) {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    <div>
                        <h2>Ticket #{selectedTicket.ticketNumber}</h2>
                        <span className={styles.statusBadge}>{selectedTicket.status}</span>
                    </div>
                    <Button label="Back to Tickets" variant="ghost" onClick={() => setView('list')} />
                </div>
                <div className={styles.messages}>
                    {selectedTicket.messages.map((msg) => (
                        <div key={msg.id} className={`${styles.message} ${msg.isCustomer ? styles.customerMsg : styles.agentMsg}`}>
                            <div className={styles.msgHeader}>
                                <strong>{msg.senderName}</strong>
                                <span>{new Date(msg.createdAt).toLocaleString()}</span>
                            </div>
                            <p>{msg.body}</p>
                        </div>
                    ))}
                </div>
                <div className={styles.replySection}>
                    <TextArea
                        label="Reply"
                        value={replyText}
                        onChange={setReplyText}
                        placeholder="Type your reply..."
                    />
                    <div className={styles.replyActions}>
                        <Button
                            label={loading ? 'Sending...' : 'Send Reply'}
                            variant="primary"
                            onClick={handleReply}
                            disabled={loading || !replyText.trim()}
                        />
                        {selectedTicket.canClose && (
                            <Button
                                label="Close Ticket"
                                variant="ghost"
                                onClick={handleCloseTicket}
                                disabled={loading}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2>{isSuperAdmin ? 'Manage Support Tickets' : 'Support Tickets'}</h2>
                {!isSuperAdmin && (
                    <Button label="New Ticket" variant="primary" onClick={() => setView('create')} />
                )}
            </div>
            {loading ? (
                <LoadingOverlay />
            ) : ticketsError ? (
                <div className={styles.errorState}>
                    <p>{ticketsError}</p>
                </div>
            ) : tickets.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No support tickets yet.</p>
                    {!isSuperAdmin && (
                        <Button label="Create Your First Ticket" variant="primary" onClick={() => setView('create')} />
                    )}
                </div>
            ) : (
                <div className={styles.ticketList}>
                    {tickets.map((ticket) => (
                        <div
                            key={ticket.id}
                            className={styles.ticketCard}
                            onClick={() => handleViewTicket(ticket.id)}
                        >
                            <div className={styles.ticketHeader}>
                                <span className={styles.ticketNumber}>#{ticket.ticketNumber}</span>
                                <span className={styles.statusBadge} style={{ backgroundColor: ticket.statusColor || 'var(--text-tertiary)' }}>
                                    {ticket.status}
                                </span>
                            </div>
                            <h3>{ticket.title || ticket.category}</h3>
                            <div className={styles.ticketMeta}>
                                <span>{ticket.category}</span>
                                <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                            </div>
                            {ticket.hasUnreadReplies && <span className={styles.unreadBadge}>New Reply</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * AB#2133 — pilot support path. Tickets proxy to Zammad, which is OFF for the
 * controlled pilot, so unless REACT_APP_FEATURE_SUPPORT_TICKETS_READY is "true"
 * the page offers no ticket creation, ticket list or chat — only the
 * founder-configured contact card (or its honest fallback). The ticket UI is
 * kept intact (dormant) for when Zammad goes live.
 */
const SupportPage: React.FC = () => {
    if (isSupportTicketsEnabled()) return <SupportTicketsPage />;
    return (
        <div className={styles.container}>
            <SupportContactCard />
        </div>
    );
};

export default SupportPage;
